import type { ViewController } from '@presentation/shell/NexxusShell';
import { fmtDate, esc } from '@presentation/shell/format';
import { applyDevisesGate } from '@presentation/shell/controllers/modulesGates';
import {
  DEVISES_MODULE,
  DEVISES_NOT_LICENSED_MESSAGE,
  isModuleLicensed,
} from '@application/licence/ModuleGate';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { MODULE_DEFINITIONS, moduleLabel } from '@application/licence/ModuleCatalog';
import { fetchAndApplyEntitlements } from '@application/licence/EntitlementFetchService';
import {
  openOfflineImportIfNeeded,
  renewalLineHtml,
} from '@presentation/views/parametres/LicenceRenewalPanel';
import { registerLicenceActions } from '@presentation/shell/controllers/parametresLicenceActions';
import { runGuardedSteps } from '@presentation/shell/controllers/parametresSafeSteps';
import { logger } from '@infra/telemetry/Logger';
import { LicenceRenewalService } from '@infra/licence/LicenceRenewalService';
import { describeRenewal } from '@infra/licence/LicenceRenewalStatus';
import {
  applyCachedEntitlementsFromTenant,
  saveEnabledModules,
} from '@application/licence/LicenceEntitlementService';
import {
  formatNetworkStatusLabel,
  NetworkStatusService,
} from '@infra/network/NetworkStatusService';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { BackupRetentionPolicy } from '@infra/backup/BackupRetentionPolicy';
import { runBackupExport, runBackupImport } from '@application/backup/BackupActions';
import { HybridVaultService } from '@application/backup/HybridVaultService';
import { HybridBackupConfigService } from '@application/backup/HybridBackupConfigService';
import { openHybridBackupSetupWizard } from '@presentation/views/parametres/hybrid/HybridBackupSetupWizard';
import { openHybridPendingRecoveryConfirm } from '@presentation/views/parametres/hybrid/HybridBackupUiBootstrap';
import { mountHybridBackupStatusBanner } from '@presentation/views/parametres/hybrid/HybridBackupStatusBanner';
import { renderHybridBackupSettingsPanel } from '@presentation/views/parametres/hybrid/HybridBackupSettingsPanel';
import { openHybridBackupRestoreDialog } from '@presentation/views/parametres/hybrid/HybridBackupRestoreView';
import { UpdateCoordinator } from '@application/update/UpdateCoordinator';
import { paintUpdateProgress } from '@presentation/shell/updateProgressUi';
import { getSqlitePersistenceStatus } from '@infra/persistence/SqlitePersistenceBridge';
import { getDeviceLabel } from '@infra/persistence/DeviceLabelStore';
import { getSyncCoordinator } from '@infra/sync/SyncCoordinator';
import { RemoteSyncStore } from '@infra/sync/RemoteSyncStore';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import { ToastService } from '@presentation/components/Toast';
import { getAppVersion } from '@shared/constants/appVersion';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import {
  DEFAULT_STC_CONFIG,
  loadStcConfig,
  saveStcConfig as persistStcConfig,
} from '@application/rh/StcConfig';
import type { StcConfig } from '@application/rh/StcConfig';
import {
  CONVENTIONS,
  appliquerConvention,
  getConventionByKey,
  getConventionActive,
} from '@application/rh/ConventionsCollectives';
import { isDemoSeedEnabled } from '@infra/licence/demoSeedConfig';
import { SeedDemoData } from '@infra/seed/SeedDemoData';
import { SeedDemoDataMassive } from '@infra/seed/SeedDemoDataMassive';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { saveTenantProfile } from '@application/persistence/TenantProfileService';
import { demanderOngletReglagesComptables } from '@presentation/views/comptabilite/ongletReglages';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import {
  companyLegalFromTenant,
  companyLegalPreviewHtml,
  isCompanyLegalComplete,
} from '@domain/legal/CompanyLegalInfo';
import type { Tenant } from '@shared/types/Tenant';
import { nifGabon, rccmGabon } from '@shared/validation/identifiantsGabon';

interface DevisesRates {
  eur: number;
  usd: number;
  gbp: number;
  chf: number;
}

const DEFAULT_DEVISES: DevisesRates = { eur: 655.957, usd: 600, gbp: 755, chf: 680 };

function legalField(section: HTMLElement, id: string): string {
  return section.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value.trim() ?? '';
}

function setLegalField(section: HTMLElement, id: string, value: string | number | undefined): void {
  const el = section.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`);
  if (el && value !== undefined && value !== null) el.value = String(value);
}

function refreshLegalPreview(section: HTMLElement, tenant: Tenant | null): void {
  const host = section.querySelector('#legal-preview');
  if (host) host.innerHTML = companyLegalPreviewHtml(tenant);
}

function bindLegalIdentityCard(
  section: HTMLElement,
  ctx: Parameters<ViewController>[1],
  canMutate: boolean,
): void {
  const tenant = ctx.tenantContext.tryGetTenant();
  if (!tenant || !section.querySelector('#legal-identity-card')) return;

  const info = companyLegalFromTenant(tenant);
  setLegalField(section, 'legal-rccm', info.rccm);
  setLegalField(section, 'legal-nif', info.nif);
  setLegalField(section, 'legal-forme', info.legal_form || 'SARL');
  setLegalField(section, 'legal-capital', info.capital);
  setLegalField(section, 'legal-raison', info.registered_name);
  setLegalField(section, 'legal-trade', info.trade_name);
  setLegalField(section, 'legal-siege', tenant.adresse ?? '');
  setLegalField(section, 'legal-ville', tenant.ville ?? '');
  setLegalField(section, 'legal-tel', info.phone);
  setLegalField(section, 'legal-email', info.email);
  setLegalField(section, 'legal-website', info.website);
  setLegalField(section, 'legal-cnss', info.cnss_employer_id);
  setLegalField(section, 'legal-cnamgs', info.cnamgs_id);
  refreshLegalPreview(section, tenant);

  const paintDraftPreview = (): void => {
    const draft: Tenant = {
      ...tenant,
      nif: legalField(section, 'legal-nif') || tenant.nif,
      rccm: legalField(section, 'legal-rccm') || tenant.rccm,
      forme_juridique: (legalField(section, 'legal-forme') ||
        tenant.forme_juridique) as Tenant['forme_juridique'],
      nom: legalField(section, 'legal-raison') || tenant.nom,
      raison_sociale: legalField(section, 'legal-raison') || tenant.raison_sociale,
      adresse: legalField(section, 'legal-siege') || tenant.adresse,
      ville: legalField(section, 'legal-ville') || tenant.ville,
      telephone: legalField(section, 'legal-tel') || tenant.telephone,
      email: legalField(section, 'legal-email') || tenant.email,
      capital_social: Number(legalField(section, 'legal-capital')) || tenant.capital_social,
      parametres: {
        ...tenant.parametres,
        trade_name: legalField(section, 'legal-trade') || undefined,
        website: legalField(section, 'legal-website') || undefined,
        numero_cnss_employeur: legalField(section, 'legal-cnss') || undefined,
        numero_cnamgs_employeur: legalField(section, 'legal-cnamgs') || undefined,
      },
    };
    refreshLegalPreview(section, draft);
  };

  section.querySelector('#legal-identity-card')?.addEventListener('input', paintDraftPreview);
  section.querySelector('#legal-identity-card')?.addEventListener('change', paintDraftPreview);

  section.querySelector('#btn-save-legal-identity')?.addEventListener('click', () => {
    void (async () => {
      if (!canMutate) {
        ToastService.warning('Action réservée à l’administrateur et au gestionnaire RH');
        return;
      }
      const nifRaw = legalField(section, 'legal-nif');
      const rccmRaw = legalField(section, 'legal-rccm');
      if (!nifRaw) {
        ToastService.error('NIF obligatoire');
        return;
      }
      if (!rccmRaw) {
        ToastService.error('RCCM obligatoire');
        return;
      }
      const nifParsed = nifGabon.safeParse(nifRaw);
      if (!nifParsed.success) {
        ToastService.error(nifParsed.error.issues[0]?.message ?? 'NIF invalide');
        return;
      }
      const rccmParsed = rccmGabon.safeParse(rccmRaw);
      if (!rccmParsed.success) {
        ToastService.error(rccmParsed.error.issues[0]?.message ?? 'RCCM invalide');
        return;
      }
      const rccmValue = rccmParsed.data;
      const raison = legalField(section, 'legal-raison');
      const siege = legalField(section, 'legal-siege');
      const forme = legalField(section, 'legal-forme');
      if (!raison || !siege || !forme) {
        ToastService.error('Dénomination, forme juridique et siège social sont obligatoires');
        return;
      }
      const session = ctx.auth.getSession?.() ?? null;
      const updated: Tenant = {
        ...tenant,
        nif: nifParsed.data,
        rccm: rccmValue,
        forme_juridique: forme as Tenant['forme_juridique'],
        nom: raison,
        raison_sociale: raison,
        adresse: siege,
        ville: legalField(section, 'legal-ville') || tenant.ville,
        telephone: legalField(section, 'legal-tel') || tenant.telephone,
        email: legalField(section, 'legal-email') || tenant.email,
        capital_social: Number(legalField(section, 'legal-capital')) || 0,
        parametres: {
          ...tenant.parametres,
          trade_name: legalField(section, 'legal-trade') || undefined,
          website: legalField(section, 'legal-website') || undefined,
          numero_cnss_employeur: legalField(section, 'legal-cnss') || undefined,
          numero_cnamgs_employeur: legalField(section, 'legal-cnamgs') || undefined,
          legal_identity_updated_at: new Date().toISOString(),
          legal_identity_updated_by: session?.user_id ?? 'admin',
        },
      };
      await saveTenantProfile(ctx, updated);
      await recordAuditEvent(ctx, {
        action: 'update',
        entity: 'entreprise',
        entity_id: updated.id,
        description: 'Informations légales mises à jour',
        payload: {
          nif: updated.nif,
          rccm: updated.rccm,
          forme_juridique: updated.forme_juridique,
        },
      });
      ToastService.success('Informations légales mises à jour');
      refreshLegalPreview(section, updated);
      if (!isCompanyLegalComplete(updated)) {
        ToastService.warning('Certains champs optionnels restent vides (CNSS / CNAMGS employeur)');
      }
    })();
  });
}

interface TiersInstitutionnel {
  id: string;
  organisme: string;
  compte: string;
  iban: string;
  banque: string;
  telephone: string;
  email: string;
}

const TIERS_INST_DEFAUT: TiersInstitutionnel[] = [
  { id: 'cnss', organisme: 'CNSS', compte: '4311', iban: '', banque: '', telephone: '', email: '' },
  {
    id: 'cnamgs',
    organisme: 'CNAMGS',
    compte: '4312',
    iban: '',
    banque: '',
    telephone: '',
    email: '',
  },
  { id: 'dgi', organisme: 'DGI', compte: '4421', iban: '', banque: '', telephone: '', email: '' },
  {
    id: 'opc',
    organisme: 'FNF / OPC formation',
    compte: '4371',
    iban: '',
    banque: '',
    telephone: '',
    email: '',
  },
];

function stcCheckbox(id: string, label: string, checked: boolean, disabled = false): string {
  return `<label style="display:flex; align-items:center; gap:8px; font-size:13px; cursor:pointer;">
    <input type="checkbox" id="${id}" ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}> ${label}</label>`;
}

function renderConventionForm(section: HTMLElement, conventionKey: string): void {
  const sel = section.querySelector<HTMLSelectElement>('#conv-select');
  if (!sel) return;
  sel.innerHTML = CONVENTIONS.map(
    (c) =>
      `<option value="${esc(c.key)}" ${c.key === conventionKey ? 'selected' : ''}>${esc(c.label)}</option>`,
  ).join('');
  const conv = getConventionByKey(conventionKey);
  const notes = section.querySelector('#conv-notes');
  if (notes) {
    notes.innerHTML = `<strong>Primes &amp; obligations sectorielles :</strong> ${esc(conv.notes)}`;
  }
}

function pctFraction(rate: number): number {
  return Math.round(rate * 1000) / 10;
}

export function renderStcForm(host: HTMLElement, config: StcConfig): void {
  const tr = config.licenciement.tranches;
  const c = config;
  host.innerHTML = `
    <div class="field" style="max-width:520px;">
      <label>Convention collective applicable</label>
      <input type="text" id="stc-convention" value="${esc(c.convention)}">
    </div>

    <h4 style="margin:18px 0 6px; color:var(--marine);">Salaire global mensuel de référence</h4>
    <p style="font-size:12px; color:var(--gris); margin:0 0 8px;">Éléments inclus dans la base de calcul des indemnités.</p>
    <div style="display:grid; grid-template-columns:repeat(auto-fill,minmax(190px,1fr)); gap:8px;">
      ${stcCheckbox('stc-base-sursalaire', 'Sursalaire', c.base.sursalaire)}
      ${stcCheckbox('stc-base-prime_anciennete', "Prime d'ancienneté", c.base.prime_anciennete)}
      ${stcCheckbox('stc-base-transport', 'Indemnité transport', c.base.transport)}
      ${stcCheckbox('stc-base-logement', 'Indemnité logement', c.base.logement)}
      ${stcCheckbox('stc-base-fonction_ind', 'Indemnité de fonction', c.base.fonction_ind)}
      ${stcCheckbox('stc-base-autres', 'Autres indemnités', c.base.autres)}
    </div>

    <h4 style="margin:20px 0 6px; color:var(--marine);">Indemnité de licenciement (barème par ancienneté)</h4>
    <div class="form-grid">
      <div class="field"><label>Ancienneté minimale (années)</label><input type="number" min="0" step="1" id="stc-lic-min" value="${c.licenciement.anciennete_min_annees}"></div>
      <div class="field"><label>Décompte</label>
        <select id="stc-lic-annees-entieres">
          <option value="1" ${c.licenciement.annees_entieres ? 'selected' : ''}>Années entières (révolues)</option>
          <option value="0" ${!c.licenciement.annees_entieres ? 'selected' : ''}>Prorata (années + fraction)</option>
        </select>
      </div>
    </div>
    <table class="data" style="width:100%; max-width:560px; margin-top:8px;">
      <thead><tr><th>Tranche d'ancienneté</th><th class="num">Jusqu'à (années)</th><th class="num">Taux (% du salaire/an)</th></tr></thead>
      <tbody>
        <tr><td>Tranche 1</td><td class="num"><input type="number" min="0" step="1" id="stc-lic-t1-j" aria-label="Tranche 1 : jusqu'à (années)" value="${tr[0]?.jusqua ?? 5}" style="width:90px;"></td><td class="num"><input type="number" min="0" step="0.5" id="stc-lic-t1-t" aria-label="Tranche 1 : taux (% du salaire par an)" value="${pctFraction(tr[0]?.taux ?? 0.2)}" style="width:90px;"></td></tr>
        <tr><td>Tranche 2</td><td class="num"><input type="number" min="0" step="1" id="stc-lic-t2-j" aria-label="Tranche 2 : jusqu'à (années)" value="${tr[1]?.jusqua ?? 10}" style="width:90px;"></td><td class="num"><input type="number" min="0" step="0.5" id="stc-lic-t2-t" aria-label="Tranche 2 : taux (% du salaire par an)" value="${pctFraction(tr[1]?.taux ?? 0.2)}" style="width:90px;"></td></tr>
        <tr><td>Tranche 3 (au-delà)</td><td class="num"><input type="number" min="0" step="1" id="stc-lic-t3-j" aria-label="Tranche 3 : jusqu'à (années)" value="${tr[2]?.jusqua ?? 999}" style="width:90px;"></td><td class="num"><input type="number" min="0" step="0.5" id="stc-lic-t3-t" aria-label="Tranche 3 : taux (% du salaire par an)" value="${pctFraction(tr[2]?.taux ?? 0.2)}" style="width:90px;"></td></tr>
      </tbody>
    </table>

    <h4 style="margin:20px 0 6px; color:var(--marine);">Préavis</h4>
    <div class="form-grid">
      <div class="field"><label for="stc-preavis-mode">Mode de calcul</label>
        <select id="stc-preavis-mode">
          <option value="anciennete_art82" ${c.preavis.mode !== 'categorie' ? 'selected' : ''}>Légal par ancienneté (Code du travail, art. 82)</option>
          <option value="categorie" ${c.preavis.mode === 'categorie' ? 'selected' : ''}>Durées fixes par catégorie (ci-dessous)</option>
        </select>
      </div>
      <div class="field"><label>Employé / ouvrier (mois)</label><input type="number" min="0" step="1" id="stc-preavis-employe" value="${c.preavis.employe}"></div>
      <div class="field"><label>Agent de maîtrise (mois)</label><input type="number" min="0" step="1" id="stc-preavis-maitrise" value="${c.preavis.maitrise}"></div>
      <div class="field"><label>Cadre / direction (mois)</label><input type="number" min="0" step="1" id="stc-preavis-cadre" value="${c.preavis.cadre}"></div>
    </div>
    <div style="margin-top:8px;">${stcCheckbox('stc-preavis-indemnise', "Verser une indemnité compensatrice si le préavis n'est pas effectué", c.preavis.indemnise_si_non_effectue)}</div>

    <h4 style="margin:20px 0 6px; color:var(--marine);">Congés payés (& report multi-années)</h4>
    <div class="form-grid">
      <div class="field"><label>Jours acquis par an</label><input type="number" min="0" step="0.5" id="stc-conges-jours-an" value="${c.conges.jours_par_an}"></div>
      <div class="field"><label>Base jours / mois (diviseur)</label><input type="number" min="1" step="1" id="stc-conges-base-jours" value="${c.conges.base_jours}"></div>
      <div class="field"><label>Mode de calcul du solde</label>
        <select id="stc-conges-report">
          <option value="1" ${c.conges.report_multi_annees !== false ? 'selected' : ''}>Report multi-années (cumul depuis l'embauche)</option>
          <option value="0" ${c.conges.report_multi_annees === false ? 'selected' : ''}>Année de sortie uniquement</option>
        </select>
      </div>
      <div class="field"><label>Plafond de report (jours, 0 = illimité)</label><input type="number" min="0" step="1" id="stc-conges-plafond" value="${c.conges.report_plafond_jours || 0}"></div>
    </div>
    <div style="margin-top:8px;">${stcCheckbox('stc-conges-deduire', 'Déduire les congés déjà pris (sinon solde acquis complet)', c.conges.deduire_conges_pris)}</div>

    <h4 style="margin:20px 0 6px; color:var(--marine);">Précarité CDD & 13e mois</h4>
    <p style="font-size:12px; color:var(--gris); margin:0 0 6px;">Le Code du travail (art. 60) ne prévoit aucune indemnité de précarité à la fin d'un CDD. N'activez cette option que si votre convention collective ou le contrat la prévoit.</p>
    <div style="margin-bottom:8px;">${stcCheckbox('stc-precarite-actif', 'Verser une prime de précarité en fin de CDD (option conventionnelle)', c.precarite_cdd.actif)}</div>
    <div class="form-grid">
      <div class="field"><label for="stc-precarite-taux">Taux précarité CDD (%) — si l'option est activée</label><input type="number" min="0" step="0.5" id="stc-precarite-taux" value="${pctFraction(c.precarite_cdd.taux)}"></div>
    </div>
    <div style="margin-top:8px; display:flex; flex-direction:column; gap:6px;">
      ${stcCheckbox('stc-13-actif', "Inclure un 13e mois / gratification de fin d'année", c.treizieme.actif)}
      ${stcCheckbox('stc-13-prorata', 'Calculer le 13e mois au prorata des mois travaillés', c.treizieme.prorata)}
    </div>`;
  associerLibellesAuxChamps(host);
}

/** Relie chaque libellé de champ à son champ (accessibilité : lecteurs d'écran, clic sur le libellé). Aucun effet visuel. */
function associerLibellesAuxChamps(host: HTMLElement): void {
  host.querySelectorAll<HTMLElement>('.field').forEach((champ) => {
    const libelle = champ.querySelector<HTMLLabelElement>('label:not([for])');
    const controle = champ.querySelector<HTMLElement>('input[id], select[id], textarea[id]');
    if (libelle && controle) libelle.setAttribute('for', controle.id);
  });
}

export function readStcForm(section: HTMLElement): StcConfig {
  const num = (id: string, fallback: number): number => {
    const v = Number(section.querySelector<HTMLInputElement>(`#${id}`)?.value);
    return Number.isFinite(v) ? v : fallback;
  };
  const chk = (id: string): boolean => !!section.querySelector<HTMLInputElement>(`#${id}`)?.checked;

  return {
    convention:
      section.querySelector<HTMLInputElement>('#stc-convention')?.value.trim() ||
      DEFAULT_STC_CONFIG.convention,
    base: {
      salaire_base: true,
      sursalaire: chk('stc-base-sursalaire'),
      prime_anciennete: chk('stc-base-prime_anciennete'),
      transport: chk('stc-base-transport'),
      logement: chk('stc-base-logement'),
      fonction_ind: chk('stc-base-fonction_ind'),
      autres: chk('stc-base-autres'),
    },
    licenciement: {
      anciennete_min_annees: num('stc-lic-min', 0),
      annees_entieres:
        section.querySelector<HTMLSelectElement>('#stc-lic-annees-entieres')?.value === '1',
      tranches: [
        { jusqua: num('stc-lic-t1-j', 5), taux: num('stc-lic-t1-t', 20) / 100 },
        { jusqua: num('stc-lic-t2-j', 10), taux: num('stc-lic-t2-t', 20) / 100 },
        { jusqua: num('stc-lic-t3-j', 999), taux: num('stc-lic-t3-t', 20) / 100 },
      ],
    },
    preavis: {
      mode:
        section.querySelector<HTMLSelectElement>('#stc-preavis-mode')?.value === 'categorie'
          ? 'categorie'
          : 'anciennete_art82',
      employe: num('stc-preavis-employe', 1),
      maitrise: num('stc-preavis-maitrise', 2),
      cadre: num('stc-preavis-cadre', 3),
      indemnise_si_non_effectue: chk('stc-preavis-indemnise'),
    },
    conges: {
      jours_par_an: num('stc-conges-jours-an', 24),
      base_jours: num('stc-conges-base-jours', 30),
      deduire_conges_pris: chk('stc-conges-deduire'),
      report_multi_annees:
        section.querySelector<HTMLSelectElement>('#stc-conges-report')?.value === '1',
      report_plafond_jours: num('stc-conges-plafond', 0),
    },
    precarite_cdd: {
      actif: chk('stc-precarite-actif'),
      taux: num('stc-precarite-taux', 7) / 100,
    },
    treizieme: { actif: chk('stc-13-actif'), prorata: chk('stc-13-prorata') },
  };
}

async function loadDevises(tenantId: string): Promise<DevisesRates> {
  return SecureTenantStore.getJson<DevisesRates>(tenantId, 'devises', DEFAULT_DEVISES);
}

function fillDevises(section: HTMLElement, rates: DevisesRates): void {
  const map: Array<[string, number]> = [
    ['devise-eur', rates.eur],
    ['devise-usd', rates.usd],
    ['devise-gbp', rates.gbp],
    ['devise-chf', rates.chf],
  ];
  for (const [id, value] of map) {
    const el = section.querySelector<HTMLInputElement>(`#${id}`);
    if (el) el.value = String(value);
  }
}

function readDevises(section: HTMLElement): DevisesRates {
  return {
    eur: Number(
      section.querySelector<HTMLInputElement>('#devise-eur')?.value ?? DEFAULT_DEVISES.eur,
    ),
    usd: Number(
      section.querySelector<HTMLInputElement>('#devise-usd')?.value ?? DEFAULT_DEVISES.usd,
    ),
    gbp: Number(
      section.querySelector<HTMLInputElement>('#devise-gbp')?.value ?? DEFAULT_DEVISES.gbp,
    ),
    chf: Number(
      section.querySelector<HTMLInputElement>('#devise-chf')?.value ?? DEFAULT_DEVISES.chf,
    ),
  };
}

function renderTiersInst(section: HTMLElement, rows: TiersInstitutionnel[]): void {
  const tbody = section.querySelector('#tiers-inst-table tbody');
  if (!tbody) return;
  tbody.innerHTML = rows
    .map(
      (r) => `<tr data-tiers-id="${esc(r.id)}">
        <td>${esc(r.organisme)}</td>
        <td><input data-f="compte" value="${esc(r.compte)}" style="width:100%;"></td>
        <td><input data-f="iban" value="${esc(r.iban)}" style="width:100%;"></td>
        <td><input data-f="banque" value="${esc(r.banque)}" style="width:100%;"></td>
        <td><input data-f="telephone" value="${esc(r.telephone)}" style="width:100%;"></td>
        <td><input data-f="email" value="${esc(r.email)}" style="width:100%;"></td>
      </tr>`,
    )
    .join('');
}

function readTiersInst(section: HTMLElement): TiersInstitutionnel[] {
  const rows = [...section.querySelectorAll<HTMLTableRowElement>('#tiers-inst-table tbody tr')];
  return rows.map((tr) => {
    const id = tr.getAttribute('data-tiers-id') ?? '';
    const def = TIERS_INST_DEFAUT.find((t) => t.id === id);
    const val = (name: string): string =>
      tr.querySelector<HTMLInputElement>(`input[data-f="${name}"]`)?.value.trim() ?? '';
    return {
      id,
      organisme: def?.organisme ?? id,
      compte: val('compte'),
      iban: val('iban'),
      banque: val('banque'),
      telephone: val('telephone'),
      email: val('email'),
    };
  });
}

/** Offres commerciales actuelles (licensing : paie, compta, paie_compta, erp). */
const OFFER_LABELS = {
  paie: 'Paie seule',
  compta: 'Comptabilité seule',
  paie_compta: 'Paie + Comptabilité',
  erp: 'ERP complet',
} as const;

function offerLabel(tier: string): string {
  const known: Readonly<Record<string, string>> = OFFER_LABELS;
  return known[tier] ?? tier.charAt(0).toUpperCase() + tier.slice(1);
}

function humanSize(bytes: number): string {
  if (bytes <= 0) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatExpiry(expSec: number): string {
  return fmtDate(new Date(expSec * 1000).toISOString());
}

function renewalWindowLabel(expSec: number, windowDays: number): string {
  const start = new Date((expSec - windowDays * 86400) * 1000);
  return `Du ${fmtDate(start.toISOString())} au ${formatExpiry(expSec)}`;
}

async function renderLicencePanel(
  section: HTMLElement,
  ctx: Parameters<ViewController>[1],
): Promise<void> {
  const licenceEl = section.querySelector('#licence-status');
  if (!licenceEl) return;

  const runtime = ctx.getLicenceRuntime();
  const state = runtime?.getState();
  const claims = state?.claims;
  const tenant = ctx.tenantContext.tryGetTenant();
  const dataMode = ctx.getLockedDataMode();
  const modeLabel =
    dataMode === 'REMOTE_MULTI_STATION' ? 'distant (multi-postes)' : 'local (hors connexion)';

  const fingerprint = (await SecureLicenceStore.getDeviceFingerprint()) ?? '—';
  const deviceLabel = await getDeviceLabel();
  const online = NetworkStatusService.isBrowserOnline();
  const remoteOnline = ctx.isRemoteOnline();

  const lines: string[] = [
    `<strong>Mode :</strong> ${esc(modeLabel)}`,
    `<strong>Entité :</strong> ${esc(tenant?.raison_sociale ?? tenant?.nom ?? '—')}`,
  ];

  if (claims) {
    lines.push(`<strong>Offre :</strong> ${esc(offerLabel(String(claims.tier)))}`);
    if (claims.trial) {
      const daysLeft = Math.max(0, Math.ceil((claims.exp - Math.floor(Date.now() / 1000)) / 86400));
      lines.push(`<strong>Essai :</strong> oui — ${daysLeft} jour(s) restant(s)`);
    }
    lines.push(`<strong>Expiration :</strong> ${esc(formatExpiry(claims.exp))}`);
    lines.push(
      `<strong>Fenêtre de renouvellement :</strong> ${esc(renewalWindowLabel(claims.exp, claims.renewal_window_days ?? 30))}`,
    );
    const renewal = describeRenewal({
      expSec: claims.exp,
      windowDays: claims.renewal_window_days ?? 30,
      nowSec: Math.floor(Date.now() / 1000),
      online,
      state: await LicenceRenewalService.getRenewalState(claims.tenant_id),
    });
    lines.push(renewalLineHtml(renewal));
    openOfflineImportIfNeeded(section, renewal.level);
    lines.push(
      `<strong>Salariés :</strong> max ${claims.max_salaries > 0 ? claims.max_salaries : 'illimité'}`,
    );
    lines.push(
      `<strong>Utilisateurs :</strong> max ${claims.max_users > 0 ? claims.max_users : 'illimité'}`,
    );
    lines.push(
      `<strong>Postes :</strong> max ${claims.max_devices > 0 ? claims.max_devices : 'illimité'}`,
    );
    const entitledModules = runtime?.getEntitledModuleCodes() ?? claims.features;
    lines.push(
      `<strong>Modules :</strong> ${esc(entitledModules.map(moduleLabel).join(', ') || '—')}`,
    );
  } else if (import.meta.env.DEV) {
    lines.push('<strong>Licence :</strong> mode développement (navigateur)');
  }

  lines.push(`<strong>Poste :</strong> ${esc(deviceLabel)}`);
  lines.push(
    `<strong>Empreinte :</strong> <span style="font-family:Consolas,monospace;font-size:11px;overflow-wrap:anywhere;">${esc(fingerprint)}</span>`,
  );

  if (isRemoteMultiStationMode(dataMode)) {
    lines.push(
      `<strong>Connexion serveur :</strong> ${remoteOnline && online ? 'connecté' : 'hors ligne — modifications mises en file jusqu’au retour du serveur'}`,
    );
  } else {
    lines.push(
      `<strong>Réseau :</strong> ${esc(formatNetworkStatusLabel(online))} (non requis en mode local)`,
    );
  }

  if (state?.isReadOnly) {
    lines.push('<strong style="color:#B45309;">État : lecture seule</strong>');
    const restriction = runtime?.getRestriction();
    if (restriction) {
      lines.push(esc(restriction.message));
      lines.push(
        'Pour relire l’état de la licence auprès du serveur Nexxus, cliquez sur « Renouveler en ligne ».',
      );
    }
  }

  licenceEl.innerHTML = lines.join('<br>');

  // A-19 — carte « Mon abonnement » (distincte du panneau licence technique)
  const tierBadge = section.querySelector('#tier-status-badge');
  const tierContent = section.querySelector('#tier-status-content');
  const tierGrid = section.querySelector('#tier-comparison-grid');
  if (tierBadge) {
    const label = claims?.tier
      ? offerLabel(String(claims.tier))
      : import.meta.env.DEV
        ? 'Dev'
        : 'Non activée';
    const active = Boolean(claims) && !state?.isReadOnly;
    tierBadge.innerHTML = `<span class="pill ${active ? 'success' : 'warning'}">${esc(label)}</span>`;
  }
  if (tierContent) {
    if (claims) {
      tierContent.innerHTML = [
        `<p style="font-size:13px;line-height:1.7;margin:0;">`,
        `<strong>Offre active :</strong> ${esc(offerLabel(String(claims.tier)))}<br>`,
        `<strong>Expiration :</strong> ${esc(formatExpiry(claims.exp))}<br>`,
        `<strong>Salariés max :</strong> ${claims.max_salaries > 0 ? claims.max_salaries : 'illimité'} · `,
        `<strong>Utilisateurs max :</strong> ${claims.max_users > 0 ? claims.max_users : 'illimité'} · `,
        `<strong>Postes max :</strong> ${claims.max_devices > 0 ? claims.max_devices : 'illimité'}<br>`,
        `<strong>Modules inclus :</strong> ${esc((runtime?.getEntitledModuleCodes() ?? claims.features).map(moduleLabel).join(', ') || '—')}`,
        `</p>`,
      ].join('');
    } else {
      tierContent.innerHTML =
        '<p style="font-size:13px;color:var(--gris);margin:0;">Aucune licence commerciale active sur ce poste. Activez une clé dans la section Licence ci-dessus.</p>';
    }
  }
  if (tierGrid) {
    const currentTier = claims?.tier ?? '';
    const tiers = [
      { id: 'paie', label: OFFER_LABELS.paie, desc: 'Paie, RH et déclarations sociales' },
      {
        id: 'compta',
        label: OFFER_LABELS.compta,
        desc: 'Comptabilité SYSCOHADA, sans ventes ni achats',
      },
      {
        id: 'paie_compta',
        label: OFFER_LABELS.paie_compta,
        desc: 'Paie et comptabilité, avec la passerelle d’écritures',
      },
      {
        id: 'erp',
        label: OFFER_LABELS.erp,
        desc: 'Toute la gestion, dont gestion commerciale, RGPD et contrôles qualité',
      },
    ];
    tierGrid.innerHTML = tiers
      .map((t) => {
        const isCurrent = currentTier === t.id;
        return `<div style="border:1px solid ${isCurrent ? 'var(--accent,#008080)' : '#DAE0E4'};border-radius:8px;padding:12px;background:${isCurrent ? 'rgba(0,128,128,.06)' : '#fff'};">
          <div style="font-weight:700;font-size:13px;">${esc(t.label)}${isCurrent ? ' · actuel' : ''}</div>
          <div style="font-size:12px;color:var(--gris);margin-top:4px;">${esc(t.desc)}</div>
        </div>`;
      })
      .join('');
  }
}

function updateModulesPreview(section: HTMLElement): void {
  const availableInputs = Array.from(
    section.querySelectorAll<HTMLInputElement>('[data-module-toggle]:not(:disabled)'),
  );
  for (const input of availableInputs) {
    const tile = input.closest<HTMLElement>('[data-module-code]');
    const description = tile?.querySelector<HTMLElement>('[data-module-state]');
    tile?.classList.toggle('is-active', input.checked);
    if (tile) tile.style.opacity = input.checked ? '1' : '.82';
    if (description) {
      description.textContent = input.checked
        ? 'Activé dans votre espace'
        : 'Disponible — actuellement désactivé';
    }
  }

  const badge = section.querySelector('#modules-count-badge');
  if (badge) {
    const selected = availableInputs.filter((input) => input.checked).length;
    badge.textContent = `${selected} actif(s) sur ${availableInputs.length} disponible(s)`;
  }
}

export function selectedModuleCodes(section: HTMLElement): string[] {
  return Array.from(
    section.querySelectorAll<HTMLInputElement>('[data-module-toggle]:checked:not(:disabled)'),
  )
    .map((input) => input.dataset.moduleCode)
    .filter((code): code is string => Boolean(code));
}

export function selectAllEntitledModules(section: HTMLElement, enabled: boolean): void {
  section
    .querySelectorAll<HTMLInputElement>('[data-module-toggle]:not(:disabled)')
    .forEach((input) => {
      input.checked = enabled;
    });
  updateModulesPreview(section);
}

export function renderModulesGrid(section: HTMLElement, ctx: Parameters<ViewController>[1]): void {
  const grid = section.querySelector('#modules-grid');
  const badge = section.querySelector('#modules-count-badge');
  if (!grid) return;

  const runtime = ctx.getLicenceRuntime();
  const entitled = new Set(runtime?.getEntitledModuleCodes() ?? []);
  const enabled = new Set(runtime?.getEnabledModuleCodes() ?? []);
  if (badge) {
    badge.textContent = `${enabled.size} actif(s) sur ${entitled.size} disponible(s)`;
  }

  grid.innerHTML = MODULE_DEFINITIONS.map((mod) => {
    const available = entitled.has(mod.code);
    const active = available && enabled.has(mod.code);
    const inputId = `module-toggle-${mod.code}`;
    const stateLabel = active
      ? 'Activé dans votre espace'
      : available
        ? 'Disponible — actuellement désactivé'
        : 'Non compris dans votre abonnement';
    return `
      <div class="module-tile ${active ? 'is-active' : ''} ${available ? '' : 'is-locked'}" data-module-code="${esc(mod.code)}" style="opacity:${active ? '1' : available ? '.82' : '.55'};">
        <div class="m-emoji">${mod.emoji}</div>
        <div class="m-body">
          <div class="m-name">${esc(mod.label)}</div>
          <div class="m-desc" data-module-state>${stateLabel}</div>
          <div class="m-views">${esc(mod.views.join(' · '))}</div>
        </div>
        <label class="module-switch" for="${inputId}" title="${available ? `Activer ou désactiver ${esc(mod.label)}` : 'Module non souscrit'}">
          <input id="${inputId}" type="checkbox" data-module-toggle data-module-code="${esc(mod.code)}" aria-label="Activer ${esc(mod.label)}" ${available ? '' : 'disabled'} ${active ? 'checked' : ''}>
          <span class="track"></span><span class="thumb"></span>
        </label>
      </div>`;
  }).join('');

  grid.querySelectorAll<HTMLElement>('[data-module-code]').forEach((tile) => {
    const input = tile.querySelector<HTMLInputElement>('[data-module-toggle]');
    if (!input || input.disabled) return;
    tile.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, label')) return;
      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input.addEventListener('change', () => updateModulesPreview(section));
  });
}

async function resolveAppVersion(): Promise<string> {
  if (window.electron?.getVersion) {
    try {
      return await window.electron.getVersion();
    } catch {
      /* fallback */
    }
  }
  return getAppVersion();
}

async function renderSyncPanel(
  section: HTMLElement,
  ctx: Parameters<ViewController>[1],
): Promise<void> {
  const card = section.querySelector('#sync-card');
  const statusEl = section.querySelector('#sync-status');
  if (!card || !statusEl) return;

  const dataMode = ctx.getLockedDataMode();
  if (!isRemoteMultiStationMode(dataMode)) {
    card.setAttribute('hidden', '');
    return;
  }
  card.removeAttribute('hidden');

  const tenantId = ctx.tenantContext.tryGetTenant()?.id;
  const browserOnline = NetworkStatusService.isBrowserOnline();
  const online = browserOnline && (await getBusinessApiClient().confirmWriteReady());
  const enabled = tenantId ? await RemoteSyncStore.isEnabled() : false;

  let pending = 0;
  if (tenantId && enabled) {
    pending = (await RemoteSyncStore.listOutbox(tenantId)).length;
  }

  statusEl.innerHTML = [
    `<strong>État :</strong> ${online ? 'en ligne' : 'hors ligne — modifications mises en file'}`,
    pending > 0
      ? `<strong>En attente :</strong> ${pending} modification(s) à envoyer`
      : '<strong>File :</strong> vide',
  ].join('<br>');

  const w = window as unknown as Record<string, unknown>;
  w['syncNow'] = async (): Promise<void> => {
    if (!tenantId) {
      ToastService.warning('Aucune entité active');
      return;
    }
    if (!online) {
      ToastService.warning('Connexion serveur requise pour synchroniser');
      return;
    }
    statusEl.textContent = 'Synchronisation en cours…';
    const btn = section.querySelector('#btn-sync-now') as HTMLButtonElement | null;
    if (btn) btn.disabled = true;
    try {
      const result = await getSyncCoordinator().syncWhenOnline(tenantId);
      if (result.pushed === 0 && result.conflicts === 0) {
        ToastService.info('Rien à synchroniser');
      }
    } finally {
      if (btn) btn.disabled = false;
      await renderSyncPanel(section, ctx);
    }
  };
}

async function renderUpdatePanel(
  section: HTMLElement,
  ctx: Parameters<ViewController>[1],
  shell: Parameters<ViewController>[2],
): Promise<void> {
  const statusEl = section.querySelector('#update-status');
  const applyBtn = section.querySelector('#btn-apply-update') as HTMLButtonElement | null;
  const upgradeBtn = section.querySelector('#btn-upgrade-latest') as HTMLButtonElement | null;
  const versionLabel = section.querySelector('#app-version-label');
  const current = await resolveAppVersion();
  if (versionLabel) versionLabel.textContent = `Nexxus ERP v${current}`;

  if (!statusEl) return;
  statusEl.textContent = `Version installée : ${current}. Cliquez sur « Mettre à jour vers la dernière version » pour vérifier et installer.`;

  const w = window as unknown as Record<string, unknown>;
  const coordinator = new UpdateCoordinator(ctx, current, () => shell.currentSession);
  coordinator.onProgress((progress) => {
    paintUpdateProgress(section, progress);
  });

  w['__updateCoordinator'] = coordinator;
  w['__pendingUpdateManifest'] = null;

  const setBusy = (busy: boolean): void => {
    if (upgradeBtn) upgradeBtn.disabled = busy;
    if (applyBtn && busy) applyBtn.disabled = true;
  };

  w['checkAppUpdate'] = async (): Promise<void> => {
    paintUpdateProgress(section, { stage: 'checking' });
    setBusy(true);
    try {
      const manifest = await coordinator.checkOnline();
      w['__pendingUpdateManifest'] = manifest?.is_outdated ? manifest : null;
      if (applyBtn) applyBtn.disabled = !manifest?.is_outdated;
      if (manifest && !manifest.is_outdated) {
        paintUpdateProgress(section, {
          stage: 'done',
          upToDate: true,
          message: `Version ${current} — déjà à jour`,
        });
        ToastService.success('Application à jour');
      } else if (manifest?.is_outdated) {
        paintUpdateProgress(section, {
          stage: 'done',
          upToDate: false,
          message: `Mise à jour ${manifest.latest_version} disponible (actuelle : ${current})`,
        });
        ToastService.info(`Mise à jour ${manifest.latest_version} disponible`);
      }
    } finally {
      setBusy(false);
    }
  };

  w['upgradeToLatestRelease'] = async (): Promise<void> => {
    setBusy(true);
    paintUpdateProgress(section, {
      stage: 'checking',
      message: 'Recherche de la dernière release…',
    });
    try {
      const manifest = await coordinator.checkOnline();
      w['__pendingUpdateManifest'] = manifest?.is_outdated ? manifest : null;
      if (applyBtn) applyBtn.disabled = !manifest?.is_outdated;
      if (!manifest?.is_outdated) {
        paintUpdateProgress(section, {
          stage: 'done',
          upToDate: true,
          message: `Version ${current} — déjà à jour`,
        });
        return;
      }
      paintUpdateProgress(section, {
        stage: 'backing_up',
        percent: 5,
        message: `Préparation de la mise à jour ${manifest.latest_version}…`,
      });
      const ok = await coordinator.applyOnline(manifest);
      if (ok) {
        statusEl.textContent =
          'Installateur lancé — suivez les étapes, puis relancez Nexxus ERP. Vos données sont conservées.';
        paintUpdateProgress(section, {
          stage: 'restarting',
          percent: 100,
          message: 'Installateur lancé — relancez Nexxus ERP après installation.',
        });
        ToastService.success('Mise à jour lancée — suivez l’installateur puis relancez l’app');
      } else {
        ToastService.error(
          'Mise à jour interrompue — réessayez ou téléchargez sur nexxus.ga/telechargement-client',
        );
        if (applyBtn) applyBtn.disabled = false;
      }
    } finally {
      setBusy(false);
    }
  };

  w['applyAppUpdate'] = async (): Promise<void> => {
    const manifest = w['__pendingUpdateManifest'] as
      import('@infra/update/UpdateService').UpdateManifest | null;
    if (!manifest?.is_outdated) {
      ToastService.warning('Aucune mise à jour en attente — vérifiez d’abord en ligne');
      return;
    }
    setBusy(true);
    paintUpdateProgress(section, {
      stage: 'backing_up',
      percent: 5,
      message: 'Préparation de la sauvegarde obligatoire…',
    });
    try {
      const ok = await coordinator.applyOnline(manifest);
      if (ok) {
        statusEl.textContent = 'Installateur lancé — suivez les étapes, puis relancez Nexxus ERP.';
        paintUpdateProgress(section, {
          stage: 'restarting',
          percent: 100,
          message: 'Installateur lancé — relancez Nexxus ERP après installation.',
        });
        ToastService.success('Mise à jour lancée — suivez l’installateur puis relancez l’app');
      } else if (applyBtn) {
        applyBtn.disabled = false;
      }
    } finally {
      setBusy(false);
    }
  };

  w['pickUsbUpdate'] = async (): Promise<void> => {
    await coordinator.applyUsbViaDialog();
  };

  w['applyUsbManifestFile'] = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    await coordinator.applyUsbManifestFile(file);
  };

  // Sélecteur de fichier du manifeste USB : lit le fichier choisi puis réarme le champ (ancien code en ligne).
  w['applyUsbManifestDepuisChamp'] = async (input: HTMLInputElement): Promise<void> => {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    await coordinator.applyUsbManifestFile(file);
  };

  // Pré-contrôle discret à l’ouverture de Paramètres (ne lance pas l’install).
  void (async () => {
    try {
      const manifest = await coordinator.checkOnline();
      w['__pendingUpdateManifest'] = manifest?.is_outdated ? manifest : null;
      if (applyBtn) applyBtn.disabled = !manifest?.is_outdated;
      if (manifest?.is_outdated) {
        statusEl.textContent = `Mise à jour ${manifest.latest_version} disponible (actuelle : ${current}). Cliquez sur « Mettre à jour vers la dernière version ».`;
      } else if (manifest) {
        statusEl.textContent = `Version ${current} — déjà à jour.`;
      }
    } catch {
      /* réseau : laisser le message par défaut */
    }
  })();
}

async function mountHybridBackupParametres(
  section: HTMLElement,
  ctx: Parameters<ViewController>[1],
): Promise<void> {
  const card = section.querySelector<HTMLElement>('#hybrid-backup-section');
  if (!card) return;

  if (!HybridVaultService.isEnabled(ctx)) {
    card.hidden = true;
    return;
  }

  card.hidden = false;
  const tenantId = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
  const bannerHost = section.querySelector<HTMLElement>('#hybrid-backup-banner-host');
  const settingsHost = section.querySelector<HTMLElement>('#hybrid-backup-settings-host');
  const setupBtn = section.querySelector<HTMLButtonElement>('#hybrid-backup-setup-btn');
  const restoreBtn = section.querySelector<HTMLButtonElement>('#hybrid-backup-restore-btn');

  const refresh = async (): Promise<void> => {
    await HybridBackupConfigService.announceState();
    if (settingsHost) await renderHybridBackupSettingsPanel(settingsHost);
    const snapshot = await HybridBackupConfigService.getSetupSnapshot();
    if (setupBtn) {
      setupBtn.hidden = snapshot.state === 'ready';
      setupBtn.textContent =
        snapshot.state === 'recovery_key_unconfirmed'
          ? 'Confirmer la clé de récupération'
          : 'Configurer la sauvegarde chiffrée';
    }
  };

  if (bannerHost) {
    mountHybridBackupStatusBanner({
      ctx,
      tenantId,
      host: bannerHost,
      onConfigure: () => {
        void openHybridBackupSetupWizard({ onCompleted: () => void refresh() });
      },
      onConfirmKey: () => {
        void openHybridPendingRecoveryConfirm({ onCompleted: () => void refresh() });
      },
    });
  }

  setupBtn?.addEventListener('click', () => {
    void (async () => {
      const snapshot = await HybridBackupConfigService.getSetupSnapshot();
      if (snapshot.state === 'recovery_key_unconfirmed' && !snapshot.replacementPending) {
        await openHybridPendingRecoveryConfirm({ onCompleted: () => void refresh() });
      } else {
        await openHybridBackupSetupWizard({ onCompleted: () => void refresh() });
      }
    })();
  });

  restoreBtn?.addEventListener('click', () => {
    void openHybridBackupRestoreDialog(ctx);
  });

  await refresh();
}

/** Texte de repli du panneau licence : jamais un simple « — » sans explication. */
function licencePanelUnavailableHtml(error: unknown): string {
  const reason = error instanceof Error && error.message ? error.message : 'erreur inattendue';
  return `<strong style="color:#B45309;">État de la licence indisponible</strong><br>${esc(reason)}<br>Utilisez « Renouveler en ligne » pour relire l’état auprès du serveur Nexxus, ou activez votre clé commerciale ci-dessous.`;
}

export const parametresController: ViewController = async (section, ctx, shell) => {
  const w = window as unknown as Record<string, unknown>;
  // Les actions Licence (Activer / Renouveler / Importer / Désactiver) sont posées AVANT tout chargement :
  // un panneau qui échoue ne doit jamais les rendre inertes.
  registerLicenceActions(w, section, ctx, async () => {
    await renderLicencePanel(section, ctx);
    renderModulesGrid(section, ctx);
  });

  const failed = await runGuardedSteps(
    [
      {
        label: 'stockage',
        run: async () => {
          const sizeEl = section.querySelector('#storage-size');
          if (sizeEl) {
            if (navigator.storage?.estimate) {
              try {
                const est = await navigator.storage.estimate();
                sizeEl.textContent = humanSize(est.usage ?? 0);
              } catch {
                sizeEl.textContent = '—';
              }
            } else {
              sizeEl.textContent = '—';
            }
          }
          const lastBackup = section.querySelector('#last-backup');
          if (lastBackup) {
            const iso = BackupRetentionPolicy.getLastExport();
            lastBackup.textContent = iso ? fmtDate(iso) : 'Jamais';
            if (BackupRetentionPolicy.isStale()) {
              lastBackup.setAttribute('title', 'Aucune sauvegarde récente (> 30 jours)');
            }
          }
          const storageRow = section.querySelector('#storage-engine');
          if (storageRow) {
            const bundle = ctx.getPersistenceBundle();
            const sqlite = await getSqlitePersistenceStatus();
            const parts = [
              bundle?.describe().engine ?? 'dexie',
              sqlite.available ? `SQLite v${sqlite.schemaVersion ?? '?'}` : null,
            ].filter(Boolean);
            storageRow.textContent = parts.join(' · ');
          }
        },
      },
      {
        label: 'droits de la licence',
        run: async () => {
          const runtime = ctx.getLicenceRuntime();
          if (runtime) {
            const synced = await fetchAndApplyEntitlements(ctx, runtime);
            if (!synced) {
              await applyCachedEntitlementsFromTenant(ctx, runtime);
            }
          }
        },
      },
      {
        label: 'licence',
        run: async () => {
          try {
            await renderLicencePanel(section, ctx);
          } catch (error) {
            const licenceEl = section.querySelector('#licence-status');
            if (licenceEl) licenceEl.innerHTML = licencePanelUnavailableHtml(error);
            throw error;
          }
        },
      },
      { label: 'modules', run: () => renderModulesGrid(section, ctx) },
      { label: 'synchronisation', run: () => renderSyncPanel(section, ctx) },
      { label: 'mises à jour', run: () => renderUpdatePanel(section, ctx, shell) },
      {
        label: 'identité légale',
        run: () => bindLegalIdentityCard(section, ctx, canMutateRecords(shell.permission)),
      },
      { label: 'sauvegarde chiffrée', run: () => mountHybridBackupParametres(section, ctx) },
    ],
    (label, error) => {
      logger.warn({
        event: 'parametres_section_failed',
        section: label,
        message: error instanceof Error ? error.message : String(error),
      });
    },
  );
  if (failed.length > 0) {
    ToastService.warning(
      `Certaines sections de Paramètres n’ont pas pu se charger : ${failed.join(', ')}. Le reste de l’écran reste utilisable.`,
    );
  }

  const tenantId = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
  const canMutate = canMutateRecords(shell.permission);
  const stcHost = section.querySelector<HTMLElement>('#stc-config-form');
  const conventionKey = await getConventionActive(tenantId);
  renderConventionForm(section, conventionKey);
  if (stcHost) renderStcForm(stcHost, await loadStcConfig(tenantId));
  fillDevises(section, await loadDevises(tenantId));
  applyDevisesGate(section, isModuleLicensed(ctx, DEVISES_MODULE));
  const tiersStored = await SecureTenantStore.getJson<TiersInstitutionnel[]>(
    tenantId,
    'tiers_institutionnels',
    TIERS_INST_DEFAUT,
  );
  renderTiersInst(section, tiersStored.length > 0 ? tiersStored : TIERS_INST_DEFAUT);
  const demoStatus = section.querySelector('#erp-demo-status');
  if (demoStatus) {
    demoStatus.textContent = isDemoSeedEnabled()
      ? 'Disponible sur ce dossier de démonstration'
      : 'Désactivé en version commerciale';
  }
  // BUG-25 — masquer boutons seed si démo désactivée
  const demoCard =
    demoStatus?.closest('.card') ?? section.querySelector('#erp-demo-status')?.parentElement;
  if (!isDemoSeedEnabled() && demoCard instanceof HTMLElement) {
    demoCard.querySelectorAll<HTMLButtonElement>('button[data-on-click*="seed"]').forEach((btn) => {
      btn.style.display = 'none';
    });
  }
  section
    .querySelectorAll<HTMLButtonElement>(
      'button[data-on-click*="seedCentresAnalytiques"], button[data-on-click*="seedAnalytique"]',
    )
    .forEach((btn) => {
      if (!isDemoSeedEnabled()) btn.style.display = 'none';
    });

  w['renderParametres'] = () => {
    void renderLicencePanel(section, ctx);
    renderModulesGrid(section, ctx);
    void renderSyncPanel(section, ctx);
    void getConventionActive(tenantId).then((key) => renderConventionForm(section, key));
    if (stcHost) void loadStcConfig(tenantId).then((cfg) => renderStcForm(stcHost, cfg));
  };

  /** Raccourci vers Comptabilité › Comptes et TVA : l'onglet est ouvert par le contrôleur Comptabilité. */
  w['ouvrirReglagesComptables'] = () => {
    demanderOngletReglagesComptables();
    void shell.navigate('comptabilite');
  };

  w['appliquerConvention'] = async (key: string) => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    const saved = await appliquerConvention(tenantId, key);
    const conv = getConventionByKey(key);
    const current = ctx.tenantContext.tryGetTenant();
    if (current) {
      await saveTenantProfile(ctx, {
        ...current,
        parametres: {
          ...current.parametres,
          convention_collective: conv.label,
        },
      });
    }
    renderConventionForm(section, key);
    if (stcHost) renderStcForm(stcHost, saved);
    ToastService.success(`Convention appliquée : ${conv.label}`);
  };

  w['saveModulesSelection'] = async (): Promise<void> => {
    const runtime = ctx.getLicenceRuntime();
    if (!runtime) {
      ToastService.error('Licence active introuvable');
      return;
    }

    const actions = section.querySelectorAll<HTMLButtonElement>('[data-module-action]');
    actions.forEach((button) => {
      button.disabled = true;
    });
    try {
      const enabled = await saveEnabledModules(ctx, runtime, selectedModuleCodes(section));
      ToastService.success(`${enabled.length} module(s) actif(s). Préférences enregistrées.`);
      renderModulesGrid(section, ctx);
    } catch (error) {
      ToastService.error(
        error instanceof Error ? error.message : "Impossible d'enregistrer les modules",
      );
    } finally {
      actions.forEach((button) => {
        button.disabled = false;
      });
    }
  };

  w['exportBackup'] = () => {
    void runBackupExport(ctx);
  };

  w['importBackup'] = () => {
    void runBackupImport(ctx);
  };

  w['selectAllModules'] = (enabled: boolean) => {
    selectAllEntitledModules(section, enabled);
  };

  w['saveStcConfig'] = async () => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    const saved = await persistStcConfig(tenantId, readStcForm(section));
    if (stcHost) renderStcForm(stcHost, saved);
    ToastService.success('Paramètres STC enregistrés');
  };

  w['resetStcConfig'] = async () => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    const saved = await appliquerConvention(tenantId, 'general');
    renderConventionForm(section, 'general');
    const sel = section.querySelector<HTMLSelectElement>('#conv-select');
    if (sel) sel.value = 'general';
    if (stcHost) renderStcForm(stcHost, saved);
    ToastService.success('Paramètres STC réinitialisés (valeurs légales)');
  };

  w['saveDevises'] = async () => {
    if (!isModuleLicensed(ctx, DEVISES_MODULE)) {
      ToastService.warning(DEVISES_NOT_LICENSED_MESSAGE);
      return;
    }
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    await SecureTenantStore.setJson(tenantId, 'devises', readDevises(section));
    ToastService.success('Taux de change enregistrés');
  };

  w['saveTiersInstitutionnels'] = async () => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    await SecureTenantStore.setJson(tenantId, 'tiers_institutionnels', readTiersInst(section));
    ToastService.success('Tiers institutionnels enregistrés');
  };

  w['resetTiersInstitutionnels'] = async () => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    await SecureTenantStore.setJson(tenantId, 'tiers_institutionnels', TIERS_INST_DEFAUT);
    renderTiersInst(section, TIERS_INST_DEFAUT);
    ToastService.success('Tiers institutionnels réinitialisés');
  };

  const runDemoSeed = async (
    kind: 'standard' | 'massive' | 'historique' | 'employees',
  ): Promise<void> => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    if (!isDemoSeedEnabled()) {
      ToastService.warning(
        'Les données de démonstration sont désactivées sur cette version commerciale.',
      );
      return;
    }
    const ok = await confirmAction({
      title: 'Données de démonstration',
      message: 'Cette opération ajoute des données fictives au dossier courant. Continuer ?',
      confirmLabel: 'Charger',
      danger: true,
      host: section,
    });
    if (!ok) return;
    if (kind === 'massive') await SeedDemoDataMassive.seed(ctx);
    else if (kind === 'standard' || kind === 'historique' || kind === 'employees') {
      await SeedDemoData.seed(ctx);
    }
    ToastService.success('Jeu de démonstration chargé — rechargement…');
    location.reload();
  };

  w['seedERPData'] = (kind: string) => {
    void runDemoSeed(kind === 'massive' ? 'massive' : 'standard');
  };
  w['seedERPHistorique'] = () => {
    void runDemoSeed('historique');
  };
  w['seedDemoEmployees'] = () => {
    void runDemoSeed('employees');
  };
  w['purgeDemoERP'] = () => {
    ToastService.warning(
      'La purge automatique n’est disponible que sur un dossier explicitement marqué démo.',
    );
  };
};
